/* eslint-disable max-lines */
/**
 * Studio AI API Client
 * Handles all API calls for video and image generation
 */

import type {
  GenerateVideoRequest,
  ImageGenerationRequest,
  ImageGenerationResult,
  MediaJob,
  StudioImageSettings,
  Video,
  VideoGenerationResult,
} from "@/features/studio-ai/types/studio-ai.types"

import { api } from "@/lib/api-client"
import { SettingsService } from "@/services/settings-service"

const STUDIO_AI_BASE = "/studio-ai"
const IMAGE_WS_IDLE_TIMEOUT_MS = 180_000

function armWebSocketIdleTimeout(
  ws: WebSocket,
  timeoutMs: number,
  onTimeout: () => void
): () => void {
  let timer: ReturnType<typeof setTimeout> | null = null

  const clear = () => {
    if (timer !== null) {
      clearTimeout(timer)
      timer = null
    }
  }

  const bump = () => {
    clear()
    timer = setTimeout(onTimeout, timeoutMs)
  }

  ws.addEventListener("message", bump)
  bump()

  return () => {
    clear()
    ws.removeEventListener("message", bump)
  }
}

/**
 * Optimize a prompt using LLM refinement
 */
export async function optimizePrompt(request: {
  prompt: string
  media_type: "image" | "video"
  size?: string
  duration?: number
  aspect_ratio?: string
  style?: string
}): Promise<{ optimized_prompt: string }> {
  return api.post<unknown, { optimized_prompt: string }>(
    `${STUDIO_AI_BASE}/optimize-prompt`,
    request
  )
}

/**
 * Generate a single video from prompt
 */
export async function generateVideo(request: GenerateVideoRequest): Promise<VideoGenerationResult> {
  const backendRequest = {
    ...request,
    refine_prompt: false,
  }
  return api.post<unknown, VideoGenerationResult>(`${STUDIO_AI_BASE}/generate`, backendRequest)
}

/**
 * Get video generation status
 */
export async function getVideoStatus(jobId: string): Promise<VideoGenerationResult> {
  return api.get<unknown, VideoGenerationResult>(`${STUDIO_AI_BASE}/status/${jobId}`)
}

/**
 * Generate an image from prompt
 */
export async function generateImage(
  request: ImageGenerationRequest
): Promise<ImageGenerationResult> {
  const backendRequest = {
    ...request,
    refine_prompt: false,
  }
  return api.post<unknown, ImageGenerationResult>(
    `${STUDIO_AI_BASE}/images/generate`,
    backendRequest
  )
}

/**
 * Edit an existing image with a text prompt.
 *
 * Streams via the /api/ws/studio-ai/images/edit WebSocket so users see partial
 * frames refine in place — same UX as image generation. Resolves with a single
 * ImageGenerationResult.
 *
 * Callers that want progress feedback pass `onPartialImage` / `onProgress`
 * callbacks; the API layer has no opinion on what to do with progress (kept
 * deliberately decoupled from the studio-ai store to avoid an api ↔ store
 * circular import).
 *
 * The mask parameter is currently ignored on the WS path — the existing edit
 * WS endpoint doesn't accept masks. Mask-based edits (the editor view's
 * inpaint flow) fall back to the REST endpoint when a mask is present.
 */
export async function editImage(request: {
  input_image: File
  prompt: string
  mask?: Blob
  size?: string
  quality?: string
  compression?: number
  output_format?: "png" | "jpeg"
  input_fidelity?: "low" | "medium" | "high"
  project_id?: string
  model_id?: string
  source_job_id?: string
  signal?: AbortSignal
  onPartialImage?: (imageUrl: string, progress: number) => void
  onStart?: () => void
  onError?: (error: string) => void
}): Promise<ImageGenerationResult> {
  request.onStart?.()

  // Mask-based edits aren't supported by the WS endpoint yet — keep REST.
  if (request.mask) {
    return editImageRest(request)
  }

  const imageBase64 = await fileToBase64(request.input_image)

  return new Promise<ImageGenerationResult>((resolve, reject) => {
    let settled = false
    let ws: WebSocket | null = null
    let cleanupIdleTimeout: (() => void) | null = null
    const signal = request.signal
    const onAbort = () => {
      // Guarded by `settled` inside finish() — if completion already won the
      // race, this becomes a no-op and we don't clobber a resolved state.
      if (settled) return
      finish(() => reject(new DOMException("Edit aborted", "AbortError")))
    }
    const finish = (action: () => void, closeNow = true) => {
      if (settled) return
      settled = true
      // Detach the abort listener so a post-completion abort can't mutate
      // state or attempt to reject the already-resolved promise.
      signal?.removeEventListener("abort", onAbort)
      cleanupIdleTimeout?.()
      if (closeNow) {
        try {
          ws?.close()
        } catch {
          // ignore — already closing/closed
        }
      } else if (ws) {
        // Success path: let the SERVER close the socket. If the client closes
        // first it races the backend's own close — the disconnected client
        // makes quart's close-send throw before it flips its `_closed` flag, so
        // quart re-closes on teardown and uvicorn logs an "Unexpected ASGI
        // message 'websocket.close'" error (one per successful edit). Waiting
        // for the server close avoids that. Fallback-close after a few seconds
        // so a hung server can never leak the socket.
        const socket = ws
        const fallback = setTimeout(() => {
          try {
            socket.close()
          } catch {
            // ignore
          }
        }, 3000)
        socket.addEventListener("close", () => clearTimeout(fallback), { once: true })
      }
      action()
    }

    if (signal) {
      if (signal.aborted) {
        onAbort()
        return
      }
      signal.addEventListener("abort", onAbort, { once: true })
    }

    ws = connectImageEditWebSocket({
      imageBase64,
      request: {
        prompt: request.prompt,
        project_id: request.project_id,
        size: request.size,
        quality: request.quality,
        compression: request.compression,
        output_format: request.output_format,
        input_fidelity: request.input_fidelity,
        model_id: request.model_id,
        source_job_id: request.source_job_id,
      },
      onPartialImage: (imageUrl, progress) => {
        request.onPartialImage?.(imageUrl, progress)
      },
      onComplete: (result) => {
        // closeNow=false: let the backend close the socket (see finish()).
        finish(() => resolve(result), false)
      },
      onError: (errorMessage) => {
        request.onError?.(errorMessage)
        finish(() => reject(new Error(errorMessage)))
      },
    })
    cleanupIdleTimeout = armWebSocketIdleTimeout(ws, IMAGE_WS_IDLE_TIMEOUT_MS, () => {
      finish(() => reject(new Error("Image edit timed out while waiting for progress")))
    })
  })
}

/** REST fallback used when the request includes a mask (inpaint flow). */
async function editImageRest(request: {
  input_image: File
  prompt: string
  mask?: Blob
  size?: string
  quality?: string
  compression?: number
  output_format?: "png" | "jpeg"
  input_fidelity?: "low" | "medium" | "high"
  project_id?: string
  model_id?: string
  source_job_id?: string
  signal?: AbortSignal
}): Promise<ImageGenerationResult> {
  const formData = new FormData()
  formData.append("image", request.input_image)
  formData.append("prompt", request.prompt)

  if (request.mask) formData.append("mask", request.mask, "mask.png")
  if (request.size) formData.append("size", request.size)
  if (request.quality) formData.append("quality", request.quality)
  if (request.compression !== undefined) formData.append("compression", String(request.compression))
  if (request.output_format) formData.append("output_format", request.output_format)
  if (request.input_fidelity) formData.append("input_fidelity", request.input_fidelity)
  if (request.project_id) formData.append("project_id", request.project_id)
  if (request.model_id) formData.append("model_id", request.model_id)
  if (request.source_job_id) formData.append("source_job_id", request.source_job_id)

  return api.post<unknown, ImageGenerationResult>(`${STUDIO_AI_BASE}/images/edit`, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
    signal: request.signal,
  })
}

/**
 * Get user's completed videos available for remixing
 */
export async function getRemixableVideos(): Promise<Video[]> {
  const response = await api.get<never, { videos: Video[] }>(`${STUDIO_AI_BASE}/videos/remixable`)
  return response.videos
}

/**
 * Response shape for POST /studio-ai/images/batch.
 *
 * Each entry is the raw image bytes the caller would otherwise pull via
 * GET /studio-ai/images/<job_id>, base64-encoded inline so the frontend
 * can decode straight to a Blob → object URL without ever attaching an
 * Authorization header. Used to fix the gallery thumbnails problem where
 * <img src> can't carry the bearer token (and EasyAuth cookies don't
 * always reach in local dev).
 */
export interface BatchImagesResponse {
  images: Array<{ job_id: string; content_type: string; data_b64: string }>
  missing: string[]
}

/**
 * Fetch many generated images in one round-trip.
 *
 * Capped at 50 ids per call by the backend (full images are larger than
 * slide thumbnails so we batch smaller than slide-sense's 100).
 */
export async function fetchImagesBatch(jobIds: string[]): Promise<BatchImagesResponse> {
  return api.post<{ job_ids: string[] }, BatchImagesResponse>(`${STUDIO_AI_BASE}/images/batch`, {
    job_ids: jobIds,
  })
}

/**
 * List all jobs for current user (videos and images)
 */
export async function listUserJobs(status?: string, mediaType?: string): Promise<MediaJob[]> {
  const params: Record<string, string> = {}
  if (status) params.status = status
  if (mediaType) params.media_type = mediaType

  const response = await api.get<never, { jobs: MediaJob[] }>(`${STUDIO_AI_BASE}/jobs`, { params })
  return response.jobs
}

/**
 * Get available settings and options
 */
export async function getSettings(): Promise<{
  video: {
    qualities: string[]
    aspect_ratios: string[]
    styles: string[]
    durations: number[]
    http_endpoint_enabled: boolean
  }
  image: {
    sizes: Array<{ value: string; label: string; description: string }>
    qualities: Array<{ value: string; label: string; description: string }>
  }
}> {
  return api.get<
    never,
    {
      video: {
        qualities: string[]
        aspect_ratios: string[]
        styles: string[]
        durations: number[]
        http_endpoint_enabled: boolean
      }
      image: {
        sizes: Array<{ value: string; label: string; description: string }>
        qualities: Array<{ value: string; label: string; description: string }>
      }
    }
  >(`${STUDIO_AI_BASE}/settings`)
}

/** A `{value,label,description}` option triple as served by the settings endpoint. */
export interface StudioSettingsOption {
  value: string
  label: string
  description?: string
}

/**
 * GET /api/studio-ai/settings — the typed view used by the model picker.
 *
 * `image.sizes` / `image.qualities` are the default model's values and stay for
 * back-compat; `image.models`, `image.default_model_id` and
 * `image.user_selectable` drive the picker. Every model field is optional so a
 * frontend deployed ahead of the backend degrades to "no picker" instead of
 * throwing.
 */
export interface StudioSettingsResponse {
  video?: {
    qualities?: string[]
    aspect_ratios?: string[]
    styles?: string[]
    durations?: number[]
    http_endpoint_enabled?: boolean
  }
  image?: StudioImageSettings & {
    sizes?: StudioSettingsOption[]
    qualities?: StudioSettingsOption[]
  }
}

/**
 * Get available settings and options, typed for the image-model picker.
 *
 * Same endpoint as `getSettings()` — that one stays as the video-capability
 * reader so the two consumers can evolve independently.
 */
export async function getStudioSettings(): Promise<StudioSettingsResponse> {
  return api.get<never, StudioSettingsResponse>(`${STUDIO_AI_BASE}/settings`)
}

/**
 * Delete a video generation job
 */
export async function deleteJob(jobId: string): Promise<{ success: boolean; message: string }> {
  return api.delete<unknown, { success: boolean; message: string }>(
    `${STUDIO_AI_BASE}/jobs/${jobId}`
  )
}

/**
 * Download a video (proxied through backend for authentication)
 */
export async function downloadVideo(jobId: string): Promise<Blob> {
  const response = await fetch(`/api${STUDIO_AI_BASE}/jobs/${jobId}/download`, {
    headers: {
      Authorization: `Bearer ${SettingsService.getInstance().token}`,
    },
  })

  if (!response.ok) {
    throw new Error("Failed to download video")
  }

  return response.blob()
}

/**
 * WebSocket connection for real-time generation updates
 */
export function connectGenerationWebSocket(params: {
  jobId: string
  mediaType?: "video" | "image"
  onProgress: (progress: number, status: string, data?: unknown) => void
  onComplete?: (result: VideoGenerationResult) => void
  onError?: (error: string) => void
  onPartialImage?: (imageUrl: string, progress: number) => void
}): WebSocket {
  const { jobId, onProgress, onComplete, onError, onPartialImage } = params
  const wsProtocol = window.location.protocol === "https:" ? "wss:" : "ws:"
  const wsHost =
    process.env.NODE_ENV === "development"
      ? `localhost:${import.meta.env.VITE_BACKEND_PORT || "50505"}`
      : window.location.host

  // Get auth token for WebSocket authentication
  const token = SettingsService.getInstance().token
  const wsUrl = `${wsProtocol}//${wsHost}/api/ws/studio-ai/status?job_id=${jobId}${token ? `&token=${token}` : ""}`

  const ws = new WebSocket(wsUrl)

  let isCompleted = false

  ws.onopen = () => {
    // Send initial request message after connection opens
    ws.send(
      JSON.stringify({
        request: {
          job_id: jobId,
        },
      })
    )
  }

  ws.onmessage = (event) => {
    try {
      const data = JSON.parse(event.data)

      // Handle different message types
      if (data.type === "heartbeat") {
        // Heartbeat - connection is alive
        return
      }

      if (data.type === "partial_image") {
        if (onPartialImage && data.image_base64) {
          const imageUrl = `data:image/png;base64,${data.image_base64}`
          onPartialImage(imageUrl, data.progress)
        }
        onProgress(data.progress, "generating", data)
      } else if (data.type === "status_update" || data.type === "progress") {
        const statusData = data.data || data
        const progress = statusData.progress || 0
        const status = String(statusData.status || data.status || "processing")
        onProgress(progress, status, statusData)
      } else if (
        data.type === "generation_completed" ||
        data.type === "image_complete" ||
        data.status === "completed"
      ) {
        isCompleted = true
        if (onComplete) {
          onComplete(data.data || data)
        }
      } else if (data.status === "failed") {
        isCompleted = true
        if (onError) {
          onError(data.error_message || "Generation failed")
        }
      } else {
        // Default handler for other messages
        const progress = data.progress || 0
        const status = String(data.status || "processing")
        onProgress(progress, status, data)
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("[WebSocket] Parse error:", error)
      if (onError) {
        onError("Failed to parse server message")
      }
    }
  }

  ws.onerror = (error) => {
    // eslint-disable-next-line no-console
    console.error(`[WebSocket] Connection error for job ${jobId}:`, error)
    if (!isCompleted && onError) {
      onError("WebSocket connection error")
    }
  }

  ws.onclose = (event) => {
    if (!isCompleted && onError && event.code !== 1000) {
      onError("Connection lost — falling back to polling")
    }
  }

  return ws
}

/**
 * Convert a File/Blob to a raw base64 string (no data: prefix).
 */
async function fileToBase64(file: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      const result = reader.result as string
      // result is "data:<mime>;base64,XXXX" — strip the prefix
      const comma = result.indexOf(",")
      resolve(comma >= 0 ? result.slice(comma + 1) : result)
    }
    reader.onerror = () => reject(reader.error ?? new Error("FileReader failed"))
    reader.readAsDataURL(file)
  })
}

/**
 * WebSocket connection for image editing with streaming.
 *
 * Mirrors connectImageGenerationWebSocket — opens /api/ws/studio-ai/images/edit,
 * sends the request payload alongside the base64-encoded source image, and
 * dispatches typed callbacks as partial frames and the final image arrive.
 */
export function connectImageEditWebSocket(params: {
  imageBase64: string
  request: {
    prompt: string
    project_id?: string
    size?: string
    quality?: string
    compression?: number
    output_format?: "png" | "jpeg"
    input_fidelity?: "low" | "medium" | "high"
    model_id?: string
    source_job_id?: string
  }
  onPartialImage: (imageUrl: string, progress: number) => void
  onComplete: (result: ImageGenerationResult) => void
  onError?: (error: string) => void
  onActivity?: (message: string) => void
  onStarted?: (jobId: string | undefined) => void
}): WebSocket {
  const { imageBase64, request, onPartialImage, onComplete, onError, onActivity, onStarted } =
    params
  const wsProtocol = window.location.protocol === "https:" ? "wss:" : "ws:"
  const wsHost =
    process.env.NODE_ENV === "development"
      ? `localhost:${import.meta.env.VITE_BACKEND_PORT || "50505"}`
      : window.location.host

  const token = SettingsService.getInstance().token
  const wsUrl = `${wsProtocol}//${wsHost}/api/ws/studio-ai/images/edit${token ? `?token=${token}` : ""}`
  const ws = new WebSocket(wsUrl)

  // Edit endpoint accepts png/jpeg only — keep MIME aligned with the request.
  const partialMime = request.output_format === "jpeg" ? "image/jpeg" : "image/png"

  let isCompleted = false
  let hasNotifiedError = false
  let cleanupIdleTimeout: (() => void) | null = null
  const notifyError = (message: string) => {
    if (!onError || isCompleted || hasNotifiedError) return
    hasNotifiedError = true
    cleanupIdleTimeout?.()
    onError(message)
  }

  ws.onopen = () => {
    ws.send(
      JSON.stringify({
        request: {
          prompt: request.prompt,
          project_id: request.project_id,
          size: request.size,
          quality: request.quality,
          compression: request.compression,
          output_format: request.output_format,
          input_fidelity: request.input_fidelity,
          model_id: request.model_id,
          source_job_id: request.source_job_id,
        },
        image: imageBase64,
      })
    )
  }

  ws.onmessage = (event) => {
    try {
      const data = JSON.parse(event.data)

      if (data.type === "heartbeat") {
        return
      }

      if (data.type === "generation_started") {
        onStarted?.(data.job_id)
      } else if (data.type === "activity") {
        if (onActivity && data.message) onActivity(data.message)
      } else if (data.type === "partial_image") {
        // Edit stream yields image_url (data URL) directly; fall back to b64 if present.
        const url =
          (typeof data.image_url === "string" && data.image_url) ||
          (data.image_base64 ? `data:${partialMime};base64,${data.image_base64}` : null)
        if (url) onPartialImage(url, data.progress ?? 0)
      } else if (
        data.type === "completed" ||
        data.type === "complete" ||
        data.type === "image_complete"
      ) {
        // The edit WS handler forwards events verbatim from
        // image_handler.stream_operation, which yields {"type": "completed"}
        // on success — note the past tense. The generate WS handler reshapes
        // to {"type": "image_complete"}. Accept all three so the same client
        // works against either endpoint and any historic naming variant.
        // Without "completed" recognized, isCompleted stays false, the close
        // handler fires with !isCompleted, and the chat shows
        // 'Image edit ended unexpectedly' even though the backend stored the
        // image and created the job document correctly.
        isCompleted = true
        cleanupIdleTimeout?.()
        onComplete(data)
      } else if (data.type === "error") {
        notifyError(data.error || data.message || "Image edit failed")
      } else if (data.type === "done" && !isCompleted) {
        notifyError("Image edit ended unexpectedly")
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("[WebSocket] Edit parse error:", error)
      notifyError("Invalid response from image edit service")
    }
  }

  ws.onerror = (error) => {
    // eslint-disable-next-line no-console
    console.error("[WebSocket] Edit connection error:", error)
    notifyError("Could not connect to image edit service")
  }

  ws.onclose = (event) => {
    cleanupIdleTimeout?.()
    if (!isCompleted) {
      const message =
        event.code === 1000
          ? "Image edit connection closed before completion"
          : "Image edit connection lost"
      notifyError(message)
    }
  }

  cleanupIdleTimeout = armWebSocketIdleTimeout(ws, IMAGE_WS_IDLE_TIMEOUT_MS, () => {
    notifyError("Image edit timed out while waiting for progress")
    ws.close()
  })

  return ws
}

/**
 * WebSocket connection for image generation with streaming
 */
export function connectImageGenerationWebSocket(params: {
  request: ImageGenerationRequest
  onPartialImage: (imageUrl: string, progress: number) => void
  onComplete: (result: ImageGenerationResult) => void
  onError?: (error: string) => void
  onActivity?: (message: string) => void
  onStarted?: (jobId: string | undefined) => void
  onProgress?: (progress: number, data?: unknown) => void
}): WebSocket {
  const { request, onPartialImage, onComplete, onError, onActivity, onStarted, onProgress } = params
  const wsProtocol = window.location.protocol === "https:" ? "wss:" : "ws:"
  const wsHost =
    process.env.NODE_ENV === "development"
      ? `localhost:${import.meta.env.VITE_BACKEND_PORT || "50505"}`
      : window.location.host

  const token = SettingsService.getInstance().token
  const wsUrl = `${wsProtocol}//${wsHost}/api/ws/studio-ai/images/generate${token ? `?token=${token}` : ""}`
  const ws = new WebSocket(wsUrl)

  // Match data-URL MIME to the user's requested output format so jpeg
  // partials render correctly in the browser instead of advertising as PNG.
  const partialMime = request.output_format === "jpeg" ? "image/jpeg" : "image/png"

  let isCompleted = false
  let hasNotifiedError = false
  let cleanupIdleTimeout: (() => void) | null = null
  const notifyError = (message: string) => {
    if (!onError || isCompleted || hasNotifiedError) return
    hasNotifiedError = true
    cleanupIdleTimeout?.()
    onError(message)
  }

  ws.onopen = () => {
    ws.send(
      JSON.stringify({
        request: {
          prompt: request.prompt,
          project_id: request.project_id,
          size: request.size,
          quality: request.quality,
          style: request.style,
          n: request.n,
          refine_prompt: false,
          compression: request.compression,
          output_format: request.output_format,
          reference_image_url: request.reference_image_url,
          background: request.background,
          style_template_id: request.style_template_id,
          style_adherence: request.style_adherence,
          model_id: request.model_id,
        },
      })
    )
  }

  ws.onmessage = (event) => {
    try {
      const data = JSON.parse(event.data)

      if (data.type === "heartbeat") {
        return
      }

      if (data.type === "generation_started") {
        onStarted?.(data.job_id)
        onProgress?.(data.progress ?? 0, data)
      } else if (data.type === "activity") {
        if (onActivity && data.message) {
          onActivity(data.message)
        }
      } else if (data.type === "partial_image") {
        onProgress?.(data.progress ?? 0, data)
        if (data.image_base64) {
          const imageUrl = `data:${partialMime};base64,${data.image_base64}`
          onPartialImage(imageUrl, data.progress)
        }
      } else if (data.type === "image_complete") {
        isCompleted = true
        cleanupIdleTimeout?.()
        onComplete(data)
      } else if (data.type === "error") {
        notifyError(data.error || data.message || "Image generation failed")
      } else if (data.type === "done" && !isCompleted) {
        notifyError("Image generation ended unexpectedly")
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("[WebSocket] Parse error:", error)
      notifyError("Invalid response from image generation service")
    }
  }

  ws.onerror = (error) => {
    // eslint-disable-next-line no-console
    console.error("[WebSocket] Connection error:", error)
    notifyError("Could not connect to image generation service")
  }

  ws.onclose = (event) => {
    cleanupIdleTimeout?.()
    if (!isCompleted) {
      const message =
        event.code === 1000
          ? "Image generation connection closed before completion"
          : "Image generation connection lost"
      notifyError(message)
    }
  }

  cleanupIdleTimeout = armWebSocketIdleTimeout(ws, IMAGE_WS_IDLE_TIMEOUT_MS, () => {
    notifyError("Image generation timed out while waiting for progress")
    ws.close()
  })

  return ws
}
